/**
 * /mi-evolucion — Centro de Evolución (vista clara, oficial).
 *
 * 7 bloques + Línea Evolutiva con paleta luminosa dominante y
 * "anclas dark" en los momentos credencial (Identidad / Mapa / Coach).
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Loader2,
  LogOut,
  Lock,
  Map as MapIcon,
  Bot,
  Activity,
  CheckCircle2,
  CircleDashed,
  CalendarClock,
  User,
  ArrowRight,
  Clock,
} from "lucide-react";
import { toast } from "sonner";
import { ARCHETYPE_META } from "@/data/quiz";
import { useArchetypeCopy, getComboFrom } from "@/data/results";
import SpiderChart from "@/components/SpiderChart";
import IdentidadCard from "@/components/IdentidadCard";
import InstallPWABanner from "@/components/InstallPWABanner";
import RegulationFlow from "@/components/RegulationFlow";
import RegulationGauge from "@/components/RegulationGauge";
import { bandForScore } from "@/data/regulation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_STORAGE_KEY = "pyg_access_token";

const STAGES = [
  { n: "01", label: "Identificación", phrase: "Así juego naturalmente bajo presión." },
  { n: "02", label: "Conciencia", phrase: "Comprendo cómo mi fortaleza también puede jugar en mi contra." },
  { n: "03", label: "Regulación", phrase: "Aprendo a gestionar mi modo sombra bajo presión." },
  { n: "04", label: "Evolución", phrase: "Practico la madurez estratégica de forma sostenida." },
];
const getCurrentStage = (ents = []) => {
  const has = (p) => ents.some((e) => e.product === p && e.status === "active");
  if (has("coach")) return 3;
  if (has("map")) return 2;
  return 1;
};

// Single source of truth for centered content width.
const CONTAINER = "max-w-[1080px] mx-auto px-6 sm:px-8";

export default function MiEvolucionPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState(null);
  const [regulation, setRegulation] = useState(null); // {latest, band, history, count} | null
  const [regFlowOpen, setRegFlowOpen] = useState(false);
  const { copy: archetypeCopy } = useArchetypeCopy();

  // Capture ?token= from the URL on first render (auto-login flow:
  // arriving from the quiz result modal or the email CTA). Store it in
  // localStorage so subsequent fetches authenticate normally, then strip
  // the token from the visible URL for cleanliness.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const urlToken = params.get("token");
      if (urlToken && urlToken.length >= 16) {
        localStorage.setItem(TOKEN_STORAGE_KEY, urlToken);
        params.delete("token");
        const cleanUrl =
          window.location.pathname +
          (params.toString() ? `?${params.toString()}` : "") +
          window.location.hash;
        window.history.replaceState({}, "", cleanUrl);
      }
    } catch {
      /* localStorage / URL parsing disabled — fall through to magic-link */
    }
  }, []);

  const fetchMe = async () => {
    let token = null;
    try { token = localStorage.getItem(TOKEN_STORAGE_KEY); } catch { /* */ }
    if (!token) { navigate("/acceder", { replace: true }); return null; }
    try {
      const { data } = await axios.get(`${API}/me`, { headers: { "X-Access-Token": token } });
      setMe(data); setLoading(false);
      // Fetch regulation in parallel (best-effort). Failure here should
      // NOT block the dashboard — the user can always take the test later.
      try {
        const { data: reg } = await axios.get(`${API}/me/regulation`, {
          headers: { "X-Access-Token": token },
        });
        setRegulation(reg);
      } catch {
        setRegulation({ latest: null, band: null, history: [], count: 0 });
      }
      return data;
    } catch {
      try {
        localStorage.removeItem(TOKEN_STORAGE_KEY);
        localStorage.removeItem(`${TOKEN_STORAGE_KEY}_meta`);
      } catch { /* */ }
      navigate("/acceder?expired=1", { replace: true });
      return null;
    }
  };

  useEffect(() => {
    let mounted = true;
    (async () => { const d = await fetchMe(); if (!mounted) return; void d; })();
    return () => { mounted = false; };
  }, []);

  const handleLogout = () => {
    try {
      localStorage.removeItem(TOKEN_STORAGE_KEY);
      localStorage.removeItem(`${TOKEN_STORAGE_KEY}_meta`);
    } catch { /* */ }
    navigate("/acceder", { replace: true });
  };

  const requestMap = async () => {
    let token = null;
    try { token = localStorage.getItem(TOKEN_STORAGE_KEY); } catch { /* */ }
    if (!token) return;
    try {
      await axios.post(`${API}/me/entitlements/request`, { product: "map" },
        { headers: { "X-Access-Token": token } });
      toast.success("Solicitud enviada. Te avisaremos por correo al activarse.");
      await fetchMe();
    } catch {
      toast.error("No pudimos registrar tu solicitud. Intenta de nuevo.");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#29BC9B]" size={28} />
      </div>
    );
  }

  const lead = me?.lead || {};
  const entitlements = me?.entitlements || [];
  const firstName = (lead.name || "").split(" ")[0] || "estratega";
  const archetype = lead.archetype || "";
  const secondary = lead.secondary || "";
  const dom = ARCHETYPE_META[archetype];
  const combo = getComboFrom(archetypeCopy, archetype, secondary);
  const mapEnt = entitlements.find((e) => e.product === "map") || null;
  const hasMap = mapEnt?.status === "active";
  const coachEnt = entitlements.find((e) => e.product === "coach") || null;
  const hasCoach = coachEnt?.status === "active";

  const evaluationDate = formatDate(lead.created_at);
  const lastSeen = formatDate(new Date().toISOString());
  const currentStage = getCurrentStage(entitlements);

  const openMap = () => {
    if (!archetype || !secondary) return;
    navigate(`/mapa/${archetype}/${secondary}`);
  };

  return (
    <div
      data-testid="mi-evolucion-page"
      className="min-h-screen bg-[#F4F4F5] text-[#0A0A0A]"
    >
      {/* Header */}
      <header className="border-b border-black/[0.06] bg-white/70 backdrop-blur-sm sticky top-0 z-10">
        <div className={`${CONTAINER} py-5 flex items-center justify-between gap-4`}>
          <Link
            to="/"
            data-testid="brand-link"
            className="text-sm tracking-[0.32em] font-semibold text-[#0A0A0A] hover:text-[#29BC9B] transition-colors shrink-0"
          >
            PLAY YOUR GAME™
          </Link>

          {/* Identity tag — Apple-editorial. Recuerda al usuario quién
              es estratégicamente cada vez que abre el dashboard.
              "Hola, Víctor · ● Halcón" — el dot toma el color del avatar. */}
          {(firstName || archetype) && (
            <div
              data-testid="identity-tag"
              className="hidden sm:flex items-center gap-2.5 text-[11px] tracking-[0.18em] uppercase text-zinc-500 font-medium truncate"
            >
              <span className="text-zinc-400">Hola,</span>
              <span className="text-[#0A0A0A] font-semibold truncate max-w-[180px]">
                {firstName}
              </span>
              {dom && (
                <>
                  <span className="text-zinc-300">·</span>
                  {dom.img ? (
                    <span
                      data-testid="identity-avatar-mini"
                      className="inline-flex items-center justify-center w-[22px] h-[22px] rounded-full shrink-0 shadow-[0_1px_3px_rgba(0,0,0,0.18)]"
                      style={{
                        background: `linear-gradient(135deg, ${dom.color || "#29BC9B"} 0%, rgba(10,10,10,0.92) 140%)`,
                      }}
                      aria-hidden
                    >
                      <img
                        src={dom.img}
                        alt=""
                        className="w-[16px] h-[16px] object-contain"
                        loading="lazy"
                        decoding="async"
                      />
                    </span>
                  ) : (
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: dom.color || "#29BC9B" }}
                      aria-hidden
                    />
                  )}
                  <span
                    className="font-semibold tracking-[0.22em]"
                    style={{ color: dom.color || "#0A0A0A" }}
                  >
                    {dom.name}
                  </span>
                </>
              )}
            </div>
          )}

          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase text-zinc-500 hover:text-zinc-900 transition-colors shrink-0"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline">Salir</span>
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className={`${CONTAINER} pt-20 pb-12`}>
        <div className="max-w-[820px]">
          <div className="text-[11px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold mb-6">
            Mi Evolución Estratégica
          </div>
          <h1
            data-testid="mi-evolucion-greeting"
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.04] tracking-tight text-[#0A0A0A]"
          >
            Hola, {firstName}.
            <br />
            <span className="text-zinc-400">
              Esta es tu identidad bajo presión.
            </span>
          </h1>
        </div>
      </section>

      {/* Regulation index — prominent baseline CTA or persistent tile */}
      <section className={`${CONTAINER} pb-6`}>
        <RegulationSection
          regulation={regulation}
          onStart={() => setRegFlowOpen(true)}
          onRepeat={() => setRegFlowOpen(true)}
        />
      </section>

      {/* Línea Evolutiva */}
      <section className={`${CONTAINER} pb-24`}>
        <EvolutionStages current={currentStage} entitlements={entitlements} />
      </section>

      {/* 01 — Tarjetón Identidad (DARK band) */}
      <DarkBand>
        <Block index="01" title="Identidad estratégica" theme="dark">
          <IdentidadCard lead={lead} />
        </Block>
      </DarkBand>

      {/* 02 — Perfil (LIGHT) */}
      <Block index="02" title="Perfil Estratégico">
        <PerfilBlock lead={lead} evaluationDate={evaluationDate} />
      </Block>

      {/* 03 — Spider (LIGHT card with dark inner panel) */}
      <Block index="03" title="Distribución estratégica">
        <SpiderBlock scores={lead.scores || {}} />
      </Block>

      {/* 04 — Resultado (LIGHT) */}
      <Block index="04" title="Resultado inicial">
        <ResultadoBlock dom={dom} combo={combo} />
      </Block>

      {/* 05 — Mapa (DARK band) */}
      <DarkBand>
        <Block index="05" title="Mapa Estratégico" theme="dark">
          <MapaBlock
            status={mapEnt?.status || "locked"}
            onRequest={requestMap}
            onOpen={openMap}
          />
        </Block>
      </DarkBand>

      {/* 06 — Coach (DARK band) */}
      <DarkBand>
        <Block index="06" title="Coach IA" theme="dark">
          <CoachBlock active={hasCoach} />
        </Block>
      </DarkBand>

      {/* 07 — Progreso (LIGHT) */}
      <Block index="07" title="Progreso">
        <ProgresoBlock
          evaluationDate={evaluationDate}
          hasMap={hasMap}
          hasCoach={hasCoach}
          lastSeen={lastSeen}
        />
      </Block>

      {/* Footer anchor */}
      <footer className="border-t border-black/[0.06] bg-white/60">
        <div className={`${CONTAINER} py-12 text-[11px] tracking-[0.22em] uppercase text-zinc-500 text-center`}>
          La presión no cambia cómo juegas.{" "}
          <span className="text-[#29BC9B]">Revela cómo juegas.</span>
        </div>
      </footer>

      {/* Contextual PWA install prompt — iOS visual / Android native. */}
      <InstallPWABanner />

      {/* Regulation assessment flow — full-screen modal, 10-sec test. */}
      <RegulationFlow
        open={regFlowOpen}
        archetype={archetype}
        token={(() => { try { return localStorage.getItem(TOKEN_STORAGE_KEY); } catch { return null; } })()}
        onComplete={() => { void fetchMe(); }}
        onClose={() => setRegFlowOpen(false)}
      />
    </div>
  );
}

// ───────────────────────── Regulation Index ───────────────────────── //
function RegulationSection({ regulation, onStart, onRepeat }) {
  // Still loading — render nothing (keeps the layout stable).
  if (regulation === null) return null;

  const latest = regulation.latest;

  // No assessment yet — prominent baseline CTA
  if (!latest) {
    return (
      <div
        data-testid="regulation-cta"
        className="relative overflow-hidden rounded-lg bg-[#050505] text-white p-8 sm:p-10 border border-black/[0.06]"
      >
        {/* Teal halo */}
        <div
          aria-hidden
          className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(41,188,155,0.18) 0%, rgba(41,188,155,0) 60%)",
            filter: "blur(30px)",
          }}
        />
        <div className="relative flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div className="max-w-[560px] space-y-3">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold">
              Tu punto de partida · 10 segundos
            </div>
            <h3 className="text-2xl sm:text-3xl font-semibold leading-tight tracking-tight">
              Antes de continuar, mide{" "}
              <span className="text-white/60">tu regulación bajo presión.</span>
            </h3>
            <p className="text-[14px] text-white/60 leading-relaxed pt-1 max-w-[520px]">
              El Avatar revela cómo juegas naturalmente. El Índice revela qué
              tan regulada está esa fortaleza <em>hoy</em>. Es la línea base
              que usaremos para medir tu evolución.
            </p>
          </div>
          <div className="shrink-0">
            <button
              onClick={onStart}
              data-testid="regulation-start-btn"
              className="group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-colors whitespace-nowrap"
            >
              Medir mi punto de partida
              <ArrowRight
                size={15}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Has an assessment — compact permanent tile + history strip
  const band = regulation.band || bandForScore(latest.score);
  const history = Array.isArray(regulation.history) ? regulation.history : [];
  const count = regulation.count || history.length || 1;

  return (
    <div className="space-y-4">
      <div
        data-testid="regulation-tile"
        className="relative overflow-hidden rounded-lg bg-[#050505] text-white p-7 sm:p-8 border border-black/[0.06] flex flex-col sm:flex-row items-center gap-8"
      >
        <div
          aria-hidden
          className="pointer-events-none absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full"
          style={{
            background: `radial-gradient(circle, ${band.color}22 0%, ${band.color}00 65%)`,
            filter: "blur(30px)",
          }}
        />
        <div className="relative shrink-0">
          <RegulationGauge
            value={latest.score}
            size={140}
            thickness={10}
            label="Índice"
            animate={false}
          />
        </div>
        <div className="relative flex-1 min-w-0 text-center sm:text-left">
          <div
            className="text-[10px] tracking-[0.32em] uppercase font-semibold"
            style={{ color: band.color }}
          >
            {band.label}
          </div>
          <h3 className="mt-2 text-xl sm:text-2xl font-semibold leading-tight tracking-tight">
            Tu Índice de Regulación Bajo Presión
          </h3>
          <p className="mt-2.5 text-[13.5px] text-white/60 leading-relaxed max-w-[520px]">
            {band.narrative}
          </p>
          <div className="mt-4 flex items-center gap-4 justify-center sm:justify-start flex-wrap">
            <div className="text-[11px] tracking-[0.22em] uppercase text-white/35 font-medium">
              Última medición · {formatDate(latest.created_at)}
            </div>
            <button
              onClick={onRepeat}
              data-testid="regulation-repeat-btn"
              className="group inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-semibold text-[#29BC9B] hover:text-white transition-colors"
            >
              Repetir medición
              <ArrowRight
                size={12}
                className="group-hover:translate-x-1 transition-transform"
              />
            </button>
          </div>
        </div>
      </div>

      <RegulationHistory history={history} count={count} />
    </div>
  );
}

// ───────────────────────── Regulation History ───────────────────────── //
function RegulationHistory({ history, count }) {
  // Only 1 measurement — subtle hint about future evolution.
  if (count <= 1) {
    return (
      <div
        data-testid="regulation-history-hint"
        className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 text-center py-3"
      >
        Tu primera medición · Repite en 30 días para ver tu evolución.
      </div>
    );
  }

  // 2+ measurements — compact evolution timeline (newest → oldest, left to right).
  // We render oldest → newest so the chronology reads naturally left to right.
  const ordered = [...history].sort((a, b) => {
    return new Date(a.created_at) - new Date(b.created_at);
  });

  return (
    <div
      data-testid="regulation-history"
      className="relative rounded-lg bg-white border border-black/[0.05] p-6 sm:p-7"
    >
      <div className="flex items-baseline justify-between mb-5">
        <div className="text-[10px] tracking-[0.32em] uppercase text-zinc-500 font-semibold">
          Evolución de tu índice
        </div>
        <div className="text-[10px] tracking-[0.22em] uppercase text-zinc-400 tabular-nums">
          {ordered.length} {ordered.length === 1 ? "medición" : "mediciones"}
        </div>
      </div>

      <ol className="space-y-3">
        {ordered.map((m, i) => {
          const band = bandForScore(m.score);
          const prev = i > 0 ? ordered[i - 1] : null;
          const delta = prev ? m.score - prev.score : null;
          const isBaseline = m.context === "baseline" || i === 0;
          return (
            <li
              key={m.id || `${m.created_at}-${i}`}
              data-testid="regulation-history-item"
              className="flex items-center gap-4 py-1"
            >
              <span
                aria-hidden
                className="inline-flex items-center justify-center w-9 h-9 rounded-full text-[12px] font-semibold tabular-nums shrink-0"
                style={{
                  backgroundColor: `${band.color}22`,
                  color: band.color,
                  border: `1.5px solid ${band.color}66`,
                }}
              >
                {m.score}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-zinc-800 font-medium">
                  {isBaseline ? "Línea base" : `Medición ${i + 1}`}{" "}
                  <span className="text-zinc-400 font-normal">
                    · {formatDate(m.created_at)}
                  </span>
                </div>
                <div className="text-[11px] tracking-[0.14em] uppercase text-zinc-500 mt-0.5">
                  {band.label}
                </div>
              </div>
              {delta !== null && (
                <span
                  className="text-[11px] tracking-[0.14em] uppercase font-semibold tabular-nums shrink-0"
                  style={{
                    color:
                      delta > 0
                        ? "#29BC9B"
                        : delta < 0
                        ? "#F07268"
                        : "#9AA0A6",
                  }}
                >
                  {delta > 0 ? "+" : ""}
                  {delta}
                  <span className="ml-1">
                    {delta > 0 ? "↑" : delta < 0 ? "↓" : "→"}
                  </span>
                </span>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ───────────────────────── Bands & Block frame ───────────────────────── //
function DarkBand({ children }) {
  return (
    <div className="relative bg-[#050505] text-white">
      {/* Soft top edge to ease the light → dark transition */}
      <div
        aria-hidden
        className="absolute -top-px left-0 right-0 h-px"
        style={{
          background:
            "linear-gradient(to right, transparent, rgba(41,188,155,0.18), transparent)",
        }}
      />
      {children}
    </div>
  );
}

function Block({ index, title, children, theme = "light" }) {
  const isDark = theme === "dark";
  return (
    <section className={`${CONTAINER} py-20`}>
      <div className="flex items-baseline gap-5 mb-10">
        <span
          className={`text-[10px] tracking-[0.32em] uppercase font-semibold tabular-nums ${
            isDark ? "text-white/35" : "text-zinc-400"
          }`}
        >
          {index}
        </span>
        <h2
          className={`text-[14px] sm:text-[15px] tracking-[0.22em] uppercase font-semibold ${
            isDark ? "text-white/75" : "text-zinc-700"
          }`}
        >
          {title}
        </h2>
        <span
          className={`flex-1 border-b mb-1 ${
            isDark ? "border-white/[0.06]" : "border-black/[0.08]"
          }`}
        />
      </div>
      {children}
    </section>
  );
}

// ───────────────────────── Línea Evolutiva ───────────────────────── //
function EvolutionStages({ current }) {
  return (
    <section
      data-testid="evolution-stages"
      data-current-stage={current}
      aria-label="Línea evolutiva"
    >
      <div className="text-[10px] tracking-[0.32em] uppercase text-zinc-500 font-semibold mb-7 text-center">
        Línea Evolutiva · Camino de desarrollo
      </div>
      <ol className="relative grid grid-cols-4 gap-0 max-w-[920px] mx-auto">
        {STAGES.map((s, i) => {
          const idx = i + 1;
          const state =
            idx < current ? "completed" : idx === current ? "current" : "upcoming";
          const isLast = i === STAGES.length - 1;
          return (
            <li
              key={s.n}
              data-testid={`stage-${s.n}`}
              data-state={state}
              className="relative pt-0 pb-1 flex flex-col items-start"
            >
              {!isLast && (
                <div
                  aria-hidden
                  className="absolute top-[11px] left-[24px] right-[-12px] h-px"
                  style={{
                    background:
                      state === "completed"
                        ? "#29BC9B"
                        : state === "current"
                        ? "linear-gradient(to right, #29BC9B 0%, rgba(0,0,0,0.10) 100%)"
                        : "transparent",
                    borderTop:
                      state === "upcoming"
                        ? "1px dashed rgba(0,0,0,0.12)"
                        : undefined,
                  }}
                />
              )}
              <Dot state={state} />
              <div
                className={`mt-5 text-[10px] tracking-[0.28em] uppercase font-semibold tabular-nums ${
                  state === "current"
                    ? "text-[#29BC9B]"
                    : state === "completed"
                    ? "text-zinc-500"
                    : "text-zinc-300"
                }`}
              >
                {s.n}
              </div>
              <div
                className={`mt-1.5 text-[14px] sm:text-[15px] font-medium ${
                  state === "current"
                    ? "text-[#0A0A0A]"
                    : state === "completed"
                    ? "text-zinc-600"
                    : "text-zinc-400"
                }`}
              >
                {s.label}
              </div>
              <div
                className={`mt-2 text-[11px] leading-snug max-w-[190px] pr-4 ${
                  state === "current"
                    ? "text-zinc-500 opacity-100"
                    : "opacity-0"
                }`}
              >
                {state === "current" ? s.phrase : ""}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function Dot({ state }) {
  if (state === "current") {
    return (
      <div className="relative w-[22px] h-[22px] flex items-center justify-center">
        <span
          aria-hidden
          className="absolute inset-0 rounded-full bg-[#29BC9B]/20 animate-ping"
        />
        <span className="relative w-[10px] h-[10px] rounded-full bg-[#29BC9B] ring-4 ring-[#29BC9B]/15" />
      </div>
    );
  }
  if (state === "completed") {
    return (
      <div className="w-[22px] h-[22px] flex items-center justify-center">
        <span className="w-[8px] h-[8px] rounded-full bg-[#29BC9B]/70" />
      </div>
    );
  }
  return (
    <div className="w-[22px] h-[22px] flex items-center justify-center">
      <span className="w-[8px] h-[8px] rounded-full border border-zinc-300 bg-white" />
    </div>
  );
}

// ───────────────────────── 02 · Perfil ───────────────────────── //
function PerfilBlock({ lead, evaluationDate }) {
  const rows = [
    { label: "Nombre", value: lead.name || "—" },
    { label: "Correo estratégico", value: lead.email || "—" },
    { label: "Evaluación inicial", value: evaluationDate },
    { label: "Empresa", value: lead.company_name || "Individual" },
  ];
  return (
    <div
      data-testid="perfil-block"
      className="grid sm:grid-cols-2 gap-x-12 gap-y-7 border border-black/[0.07] rounded-lg p-9 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
    >
      {rows.map((r) => (
        <div key={r.label} className="space-y-2">
          <div className="text-[10px] tracking-[0.28em] uppercase text-zinc-500 font-semibold">
            {r.label}
          </div>
          <div className="text-[15px] text-zinc-800">{r.value}</div>
        </div>
      ))}
    </div>
  );
}

// ───────────────────────── 03 · Spider ───────────────────────── //
function SpiderBlock({ scores }) {
  return (
    <div
      data-testid="spider-block"
      className="border border-black/[0.07] rounded-lg p-5 sm:p-6 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)] flex justify-center"
    >
      <div className="rounded-md bg-[#050505] p-6 sm:p-8 w-full max-w-[560px] aspect-square flex items-center justify-center">
        <div className="w-full h-full">
          <SpiderChart scores={scores} />
        </div>
      </div>
    </div>
  );
}

// ───────────────────────── 04 · Resultado ───────────────────────── //
function ResultadoBlock({ dom, combo }) {
  if (!dom) {
    return (
      <p className="text-zinc-500 text-[14px]">
        Pronto verás aquí tu lectura inicial.
      </p>
    );
  }
  return (
    <div
      data-testid="resultado-inicial-block"
      className="border border-black/[0.07] rounded-lg p-9 sm:p-10 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)] space-y-6 max-w-[780px] mx-auto"
    >
      <div className="text-[10px] tracking-[0.28em] uppercase text-[#29BC9B] font-semibold">
        Tu lectura estratégica
      </div>
      <p className="text-[17px] sm:text-[18px] text-zinc-800 leading-[1.65]">
        {combo ? combo.body : dom.description}
      </p>
      <p className="text-[13px] text-zinc-500 leading-relaxed">
        Esta lectura es el punto de partida de tu evolución. Sirve para
        reconocer cómo juegas cuando aumenta la exigencia, antes de aprender a
        regularlo.
      </p>
    </div>
  );
}

// ───────────────────────── 05 · Mapa ───────────────────────── //
function MapaBlock({ status, onRequest, onOpen }) {
  const includes = [
    "Tu ventaja estratégica natural",
    "Tu modo sombra",
    "Los disparadores más frecuentes",
    "Estrategias de regulación",
    "Tu camino de evolución estratégica",
  ];
  const isActive = status === "active";
  const isRequested = status === "requested";
  const statusLabel = isActive
    ? "Acceso activo"
    : isRequested
    ? "Solicitud en revisión"
    : "Acceso bloqueado";
  return (
    <LockableDark
      testId="mapa-block"
      active={isActive}
      icon={<MapIcon size={20} />}
      lockedIcon={isRequested ? <Clock size={18} /> : null}
      stage="Etapa 02 · Conciencia"
      title="Mapa Estratégico Completo"
      subtitle="Comprende cómo tu fortaleza natural puede convertirse en tu principal riesgo bajo presión."
      includes={includes}
      value="USD 7"
      statusLabel={statusLabel}
      action={
        isActive ? (
          <button
            data-testid="mapa-open-btn"
            onClick={onOpen}
            className="group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-all"
          >
            Abrir mi Mapa Estratégico
            <ArrowRight
              size={15}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
        ) : isRequested ? (
          <div
            data-testid="mapa-requested-pill"
            className="inline-flex items-center gap-2 bg-white/[0.04] border border-white/10 text-white/60 text-[12px] tracking-[0.22em] uppercase font-medium px-5 py-3.5 rounded-sm"
          >
            <Clock size={14} className="text-[#29BC9B]" />
            Solicitud en revisión
          </div>
        ) : (
          <button
            data-testid="mapa-unlock-btn"
            onClick={onRequest}
            className="group inline-flex items-center gap-3 bg-[#29BC9B] hover:bg-[#22a989] text-[#050505] text-[12px] tracking-[0.22em] uppercase font-bold px-7 py-4 rounded-sm transition-all"
          >
            Desbloquear mi Mapa Estratégico
            <ArrowRight
              size={15}
              className="group-hover:translate-x-1 transition-transform"
            />
          </button>
        )
      }
      actionHint={
        isRequested
          ? "Te avisaremos por correo en cuanto se active."
          : null
      }
    />
  );
}

// ───────────────────────── 06 · Coach ───────────────────────── //
function CoachBlock({ active }) {
  const includes = [
    "Entrenamiento personalizado por avatar",
    "Escenarios reales de presión",
    "Identificación de patrones",
    "Desarrollo de claridad estratégica",
    "Seguimiento de evolución",
  ];
  return (
    <LockableDark
      testId="coach-block"
      active={active}
      icon={<Bot size={20} />}
      stage="Etapa 03 · Regulación"
      title="Coach IA"
      subtitle="Entrena conversaciones guiadas para desarrollar claridad, intención y madurez bajo presión."
      includes={includes}
      statusLabel={active ? "Entrenamiento disponible" : "Acceso bloqueado"}
    />
  );
}

function LockableDark({
  testId, active, icon, lockedIcon, stage, title, subtitle,
  includes, value, statusLabel, action, actionHint,
}) {
  return (
    <div
      data-testid={testId}
      data-active={active ? "true" : "false"}
      className={`relative border rounded-lg p-8 sm:p-10 ${
        active
          ? "border-[#29BC9B]/40 bg-[#29BC9B]/[0.04]"
          : "border-white/[0.08] bg-white/[0.02]"
      }`}
    >
      <div className="flex items-start justify-between gap-6 mb-6">
        <div className="flex items-start gap-5">
          <div
            className={`w-11 h-11 rounded-sm flex items-center justify-center shrink-0 ${
              active
                ? "bg-[#29BC9B] text-[#050505]"
                : "bg-white/[0.04] text-white/40 border border-white/10"
            }`}
          >
            {active ? icon : lockedIcon || <Lock size={18} />}
          </div>
          <div className="space-y-2.5">
            {stage && (
              <div
                data-testid={`${testId}-stage`}
                className="text-[10px] tracking-[0.32em] uppercase text-[#29BC9B] font-semibold"
              >
                {stage}
              </div>
            )}
            <h3 className="text-xl sm:text-2xl font-semibold tracking-tight leading-tight">
              {title}
            </h3>
            <p className="text-[14px] text-white/55 leading-relaxed max-w-[540px]">
              {subtitle}
            </p>
          </div>
        </div>
        <span
          className={`shrink-0 text-[10px] tracking-[0.24em] uppercase font-bold px-3 py-1.5 rounded-sm whitespace-nowrap ${
            active
              ? "bg-[#29BC9B] text-[#050505]"
              : "bg-white/[0.05] text-white/50 border border-white/10"
          }`}
        >
          {statusLabel}
        </span>
      </div>

      <div className="pl-16 space-y-3">
        <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 font-semibold mb-2">
          Incluye
        </div>
        {includes.map((line) => (
          <div
            key={line}
            className="flex items-start gap-3 text-[14px] text-white/75"
          >
            <span
              className={`mt-2 inline-block w-1 h-1 rounded-full ${
                active ? "bg-[#29BC9B]" : "bg-white/30"
              }`}
            />
            <span>{line}</span>
          </div>
        ))}
      </div>

      <div className="mt-8 pl-16 pt-6 border-t border-white/[0.06] grid sm:grid-cols-2 gap-6">
        {value && (
          <div className="space-y-1.5">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 font-semibold">
              Valor
            </div>
            <div
              data-testid={`${testId}-value`}
              className="text-[15px] text-white/85 font-medium"
            >
              {value}
            </div>
          </div>
        )}
        <div className="space-y-1.5">
          <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 font-semibold">
            Estado actual
          </div>
          <div
            data-testid={`${testId}-status`}
            className={`text-[15px] font-medium ${
              active ? "text-[#29BC9B]" : "text-white/85"
            }`}
          >
            {statusLabel}
          </div>
        </div>
      </div>

      {action && <div className="mt-7 pl-16">{action}</div>}
      {actionHint && (
        <div className="mt-2.5 pl-16 text-[12px] text-white/45 leading-relaxed">
          {actionHint}
        </div>
      )}
    </div>
  );
}

// ───────────────────────── 07 · Progreso ───────────────────────── //
function ProgresoBlock({ evaluationDate, hasMap, hasCoach, lastSeen }) {
  const items = [
    { label: "Evaluación inicial completada", sub: evaluationDate, done: true, Icon: User },
    {
      label: hasMap ? "Mapa Estratégico activado" : "Mapa Estratégico pendiente",
      sub: hasMap ? "Acceso activo" : "—",
      done: hasMap,
      Icon: MapIcon,
    },
    {
      label: hasCoach ? "Coach IA activado" : "Coach IA pendiente",
      sub: hasCoach ? "Entrenamiento disponible" : "—",
      done: hasCoach,
      Icon: Bot,
    },
    { label: "Último acceso", sub: lastSeen, done: true, Icon: CalendarClock },
  ];
  return (
    <div
      data-testid="progreso-block"
      className="border border-black/[0.07] rounded-lg p-9 bg-white shadow-[0_1px_2px_rgba(0,0,0,0.02)]"
    >
      <ol className="space-y-6">
        {items.map((it, i) => (
          <li
            key={i}
            className="flex items-start gap-5 pb-6 last:pb-0 border-b last:border-0 border-black/[0.06]"
          >
            <div
              className={`w-10 h-10 rounded-sm shrink-0 flex items-center justify-center ${
                it.done
                  ? "bg-[#29BC9B]/12 text-[#29BC9B] border border-[#29BC9B]/30"
                  : "bg-zinc-100 text-zinc-400 border border-black/[0.07]"
              }`}
            >
              {it.done ? <CheckCircle2 size={18} /> : <CircleDashed size={18} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[15px] text-zinc-800 font-medium">
                {it.label}
              </div>
              <div className="text-[12px] text-zinc-500 mt-1">{it.sub}</div>
            </div>
            <it.Icon size={16} className="text-zinc-300 shrink-0 mt-2.5" />
          </li>
        ))}
      </ol>
      <div className="mt-7 pt-6 border-t border-black/[0.06] flex items-start gap-3 text-[12px] text-zinc-500">
        <Activity size={14} className="mt-0.5 text-[#29BC9B] shrink-0" />
        <span>
          Tu Centro de Evolución registrará cada paso. Esta sección crecerá a
          medida que evoluciones.
        </span>
      </div>
    </div>
  );
}

// ───────────────────────── Helpers ───────────────────────── //
function formatDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("es-ES", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  } catch {
    return "—";
  }
}
